import type {
  CreateDepartmentFormDTO,
  UpdateDepartmentFormDTO,
} from "@/modules/departments/domain/contracts";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { config } from "@/shared/lib/config";

const baseUrl = `${config.apiUrl}/departments`;

export const departmentsApi = {
  async list(query: Partial<OffsetPageQueryDTO> = {}) {
    const params = new URLSearchParams();
    if (query.limit) params.set("limit", String(query.limit));
    if (query.offset) params.set("offset", String(query.offset));
    if (query.sort) params.set("sort", JSON.stringify(query.sort));
    if (query.filters) params.set("filters", JSON.stringify(query.filters));

    const url = `${baseUrl}?${params.toString()}`;
    const res = await fetch(url, {
      credentials: "include",
    });
    if (!res.ok)
      throw new Error(`Failed to fetch departments: ${res.statusText}`);
    return res.json();
  },

  async get(id: string) {
    const res = await fetch(`${baseUrl}/${id}`, {
      credentials: "include",
    });
    if (!res.ok)
      throw new Error(`Failed to fetch department: ${res.statusText}`);
    return res.json();
  },

  async getPositions(id: string, query: Partial<OffsetPageQueryDTO> = {}) {
    const params = new URLSearchParams();
    if (query.limit) params.set("limit", String(query.limit));
    if (query.offset) params.set("offset", String(query.offset));
    if (query.sort) params.set("sort", JSON.stringify(query.sort));
    if (query.filters) params.set("filters", JSON.stringify(query.filters));

    const url = `${baseUrl}/${id}/positions?${params.toString()}`;
    const res = await fetch(url, {
      credentials: "include",
    });
    if (!res.ok)
      throw new Error(`Failed to fetch positions: ${res.statusText}`);
    return res.json();
  },

  async create(input: CreateDepartmentFormDTO) {
    const formData = new FormData();
    formData.set("name", input.name);
    if (input.managerId) formData.set("managerId", input.managerId);

    const res = await fetch(baseUrl, {
      method: "POST",
      body: formData,
      credentials: "include",
    });
    if (!res.ok) {
      const error = await res.json().catch(() => ({ error: res.statusText }));
      throw new Error(
        error.error || `Failed to create department: ${res.statusText}`,
      );
    }
    return res.json();
  },

  async update(id: string, input: UpdateDepartmentFormDTO) {
    const formData = new FormData();
    if (input.name) formData.set("name", input.name);
    // Handle managerId: send value if provided, empty string if explicitly null (to clear)
    if (input.managerId !== undefined) {
      formData.set("managerId", input.managerId ?? "");
    }

    const res = await fetch(`${baseUrl}/${id}`, {
      method: "PUT",
      body: formData,
      credentials: "include",
    });
    if (!res.ok) {
      const error = await res.json().catch(() => ({ error: res.statusText }));
      throw new Error(
        error.error || `Failed to update department: ${res.statusText}`,
      );
    }
    return res.json();
  },

  async remove(id: string) {
    const res = await fetch(`${baseUrl}/${id}`, {
      method: "DELETE",
      credentials: "include",
    });
    if (!res.ok) {
      const error = await res.json().catch(() => ({ error: res.statusText }));
      throw new Error(
        error.error || `Failed to delete department: ${res.statusText}`,
      );
    }
    return res.json();
  },
};
